/**
 * Recipe JSON Schema for Gemini responseSchema.
 *
 * Used across all recipe creation paths (paste import, studio generation, modification)
 * to request structured JSON output from Gemini instead of free-form markdown. This
 * eliminates the regex-based parser as the primary extraction mechanism.
 *
 * Format notes:
 * - Gemini's responseSchema uses an OpenAPI 3.0 schema subset with UPPERCASE type names
 *   (STRING, OBJECT, ARRAY, etc.). Field names use camelCase or snake_case as preferred.
 * - All fields are `nullable: true` so the model can omit them rather than fabricate
 *   empty strings; the parser then maps missing fields to undefined/empty defaults.
 * - Keep the schema minimal — it constrains output structure, not content. Style
 *   guidance (metric/imperial, UK English, sub-groupings) lives in the prompt.
 */

export const RECIPE_CATEGORIES = [
  'Asian',
  'Sri Lankan',
  'Mexican',
  'Latin American',
  'Desserts',
  'Drinks',
  'Sauces',
  'To Try',
] as const;

export type RecipeCategoryLiteral = (typeof RECIPE_CATEGORIES)[number];

/**
 * Gemini-compatible JSON Schema for a single recipe.
 * The model is expected to return an object matching this shape when
 * generationConfig.response_mime_type = 'application/json'.
 */
export const RECIPE_RESPONSE_SCHEMA = {
  type: 'OBJECT',
  properties: {
    title: {
      type: 'STRING',
      description: 'Short, appetizing recipe title. No dietary tags like "Low-Carb" or "Gluten-Free".',
      nullable: true,
    },
    description: {
      type: 'STRING',
      description: '1-2 sentence description of the dish. Always include even if the source has none.',
      nullable: true,
    },
    ingredients: {
      type: 'STRING',
      description:
        'All ingredients as a markdown string. Use "-" for bullets, "**For the X:**" for sub-groupings. Preserve original order.',
      nullable: true,
    },
    method: {
      type: 'STRING',
      description: 'All method/instructions as a markdown string. Use numbered list "1.", "2.", etc. Preserve sub-groupings.',
      nullable: true,
    },
    notes: {
      type: 'STRING',
      description: 'Supplementary notes: tips, storage, variations. May be empty.',
      nullable: true,
    },
    category: {
      type: 'STRING',
      enum: RECIPE_CATEGORIES as unknown as string[],
      description: 'One of the 8 valid categories. OMIT the field entirely if the recipe does not clearly fit any category.',
      nullable: true,
    },
    structured: {
      type: 'OBJECT',
      description:
        'Optional schema-v2 structured layer (servings, times, equipment, structured ingredients, timed steps). See RECIPE_STRUCTURED_APPENDIX_PROMPT for the exact shape. May be omitted.',
      nullable: true,
    },
  },
} as const;

/**
 * Standard responseFormat payload. Venice (the only provider) does not support
 * response_schema, so the gateway drops this and the caller falls back to the
 * markdown parsers. Kept for the JSON-first parse path when a provider supports it.
 */
export const RECIPE_JSON_RESPONSE_FORMAT = {
  mimeType: 'application/json',
  schema: RECIPE_RESPONSE_SCHEMA,
} as const;

/**
 * P1.2 — Structured appendix contract (prompt-driven schema v2).
 *
 * Venice ignores responseSchema, so the structured layer is requested via the
 * PROMPT: the model writes the canonical markdown recipe FIRST, then appends a
 * fenced `recipe-data` JSON block. The parser extracts the block separately and
 * strips it from the markdown so the canonical fields never see JSON.
 *
 * Shape mirrors `RecipeStructured` (types/recipe.ts) and PRD Section 7.1.
 * Injected once by the studio generation prompt (not per-persona — P1.4 folds
 * it into the shared output contract).
 */
export const RECIPE_STRUCTURED_APPENDIX_PROMPT = `
**STRUCTURED DATA APPENDIX — MANDATORY:** The response is INCOMPLETE without it. Immediately after the Notes section (nothing in between), append a fenced code block labeled \`recipe-data\` containing ONLY this JSON object (no commentary, no markdown inside the JSON values):

\`\`\`recipe-data
{
  "servings": { "min": 4, "max": 4 },
  "times": { "prepMinutes": 15, "cookMinutes": 30 },
  "equipment": ["12-inch nonstick skillet"],
  "ingredients": [
    { "qty": 450, "unit": "g", "imperial": "1 lb", "item": "ground turkey", "note": "preferably 93% lean", "group": null }
  ],
  "steps": [
    { "text": "Step text mirroring the markdown method step", "timingMinutes": 8 }
  ]
}
\`\`\`

Rules:
- "servings" and "times": whole minutes/numbers; use null only if genuinely unknowable.
- "equipment": every notable tool/pan once; empty array if none.
- "ingredients": one entry per markdown ingredient, same order; qty is a number (null for "to taste"); unit is the metric unit string; imperial is the display string from the parentheses ("1 lb"), null if absent; group matches the markdown sub-heading, else null.
- "steps": one entry per markdown method step, same order and same text; timingMinutes is the estimated active/passive minutes for that step, null if not meaningful.
- The markdown recipe above the block remains the complete recipe — the JSON is machine-readable metadata, not a replacement.
- Do NOT omit this block. Do NOT wrap it in prose. It must be the last thing in your output.`;
